import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Users, Mail, LogIn, AlertTriangle, Send, Loader2, Eye, Newspaper, Clock, Zap, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import AuthModal from "@/components/AuthModal";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Breaking news form
  const [breakingTitle, setBreakingTitle] = useState("");
  const [breakingMessage, setBreakingMessage] = useState("");
  const [breakingUrl, setBreakingUrl] = useState("");
  const [breakingCategory, setBreakingCategory] = useState("");
  const [sendingBreaking, setSendingBreaking] = useState(false);
  const [breakingResults, setBreakingResults] = useState(null);

  // Digest preview
  const [digestPreview, setDigestPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Read tracking stats
  const [readStats, setReadStats] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (user && user.role === "admin") {
      fetchSubscribers();
      fetchDigestPreview();
      fetchReadStats();
    }
  }, [user, authLoading]);

  const fetchSubscribers = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/subscribers`, { headers: authHeaders() });
      setSubscribers(data.subscribers);
    } catch (error) {
      // Subscriber fetch error handled silently
    }
    finally { setLoading(false); }
  };

  const fetchDigestPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/digest-preview`, { headers: authHeaders() });
      setDigestPreview(data);
    } catch (err) {
      console.error("Failed to load digest preview:", err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const fetchReadStats = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/read-stats`, { headers: authHeaders() });
      setReadStats(data);
    } catch (err) {
      console.error("Failed to load read stats:", err);
    }
  };

  const downloadCSV = () => { window.open(`${BACKEND_URL}/api/admin/subscribers/export`, '_blank'); };

  const handleSendBreaking = async (e) => {
    e.preventDefault();
    if (!breakingTitle.trim() || !breakingMessage.trim()) {
      toast.error("Title and message are required");
      return;
    }
    setSendingBreaking(true);
    setBreakingResults(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/breaking-news`, {
        title: breakingTitle,
        message: breakingMessage,
        article_url: breakingUrl || null,
        category: breakingCategory || null
      }, { headers: authHeaders() });
      setBreakingResults(data.results);
      toast.success("Breaking news sent!");
      setBreakingTitle("");
      setBreakingMessage("");
      setBreakingUrl("");
      setBreakingCategory("");
    } catch (error) {
      toast.error("Failed to send breaking news");
    } finally { setSendingBreaking(false); }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--color-bg)" }}>
        <p style={{ color: "var(--color-text-muted)" }}>Loading...</p>
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--color-bg)" }}>
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 mb-8 transition-colors" style={{ color: "var(--color-text-dim)" }}>
            <ArrowLeft className="w-5 h-5" /> Back to News
          </button>
          <div className="rounded-xl p-8" style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}>
            <Users className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--color-accent)" }} />
            <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>Admin Access Required</h2>
            <p className="mb-6" style={{ color: "var(--color-text-muted)" }}>
              {!user ? "Please sign in with an admin account." : "Your account does not have admin privileges."}
            </p>
            {!user && (
              <Button onClick={() => setShowAuthModal(true)} className="text-white flex items-center gap-2" style={{ backgroundColor: "var(--color-accent)" }} data-testid="admin-signin-button">
                <LogIn className="w-4 h-4" /> Sign In as Admin
              </Button>
            )}
          </div>
        </div>
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--color-bg)" }}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 mb-6 transition-colors" style={{ color: "var(--color-text-dim)" }}>
          <ArrowLeft className="w-5 h-5" /> Back to News
        </button>

        {/* Breaking News Sender */}
        <div className="rounded-xl p-8 mb-8" style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-red-500">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-black" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
                Send Breaking News
              </h2>
              <p className="text-sm" style={{ color: "var(--color-text-dim)" }}>
                Notify all subscribers via push notification + email
              </p>
            </div>
          </div>

          <form onSubmit={handleSendBreaking} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-muted)" }}>Headline</label>
              <Input value={breakingTitle} onChange={(e) => setBreakingTitle(e.target.value)} placeholder="Breaking news headline..."
                className="rounded-lg" style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
                data-testid="breaking-title-input" required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-muted)" }}>Message</label>
              <Textarea value={breakingMessage} onChange={(e) => setBreakingMessage(e.target.value)} placeholder="Details about the breaking news..."
                className="rounded-lg min-h-[100px]" style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
                data-testid="breaking-message-input" required />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-muted)" }}>Article URL (optional)</label>
                <Input value={breakingUrl} onChange={(e) => setBreakingUrl(e.target.value)} placeholder="https://..."
                  className="rounded-lg" style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
                  data-testid="breaking-url-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-muted)" }}>Category (optional)</label>
                <Input value={breakingCategory} onChange={(e) => setBreakingCategory(e.target.value)} placeholder="e.g. us-politics"
                  className="rounded-lg" style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
                  data-testid="breaking-category-input" />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button type="submit" disabled={sendingBreaking} className="text-white flex items-center gap-2 px-6"
                style={{ backgroundColor: "#ef4444" }} data-testid="send-breaking-button">
                {sendingBreaking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sendingBreaking ? "Sending..." : "Send Breaking News"}
              </Button>
              {breakingResults && (
                <div className="text-sm" style={{ color: "var(--color-text-muted)" }} data-testid="breaking-results">
                  Push: {breakingResults.push_sent} sent • Email: {breakingResults.email_sent} sent
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Digest Preview */}
        <div className="rounded-xl p-8 mb-8" style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }} data-testid="digest-preview-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--color-accent)" }}>
                <Eye className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-black" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
                  Next Digest Preview
                </h2>
                <p className="text-sm" style={{ color: "var(--color-text-dim)" }}>
                  Preview what subscribers will receive in the next Daily News Update
                </p>
              </div>
            </div>
            <Button onClick={fetchDigestPreview} disabled={loadingPreview} className="flex items-center gap-2"
              style={{ backgroundColor: "var(--color-accent)", color: "#fff" }} data-testid="refresh-preview-button">
              {loadingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              Refresh
            </Button>
          </div>

          {loadingPreview && !digestPreview ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--color-accent)" }} />
            </div>
          ) : digestPreview ? (
            <div>
              {/* Digest metadata */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg" style={{ backgroundColor: "var(--color-card-hover)", border: "1px solid var(--color-border)" }}>
                  <Clock className="w-4 h-4" style={{ color: "var(--color-accent)" }} />
                  <div>
                    <p className="text-xs" style={{ color: "var(--color-text-dim)" }}>Next Send</p>
                    <p className="text-sm font-bold" style={{ color: "var(--color-text)" }} data-testid="next-digest-time">
                      {digestPreview.next_digest_time} • {digestPreview.next_digest_date}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg" style={{ backgroundColor: "var(--color-card-hover)", border: "1px solid var(--color-border)" }}>
                  <Users className="w-4 h-4" style={{ color: "var(--color-accent)" }} />
                  <div>
                    <p className="text-xs" style={{ color: "var(--color-text-dim)" }}>Recipients</p>
                    <p className="text-sm font-bold" style={{ color: "var(--color-text)" }} data-testid="subscriber-count">
                      {digestPreview.subscriber_count} subscriber{digestPreview.subscriber_count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg" style={{ backgroundColor: "var(--color-card-hover)", border: "1px solid var(--color-border)" }}>
                  <Newspaper className="w-4 h-4" style={{ color: "var(--color-accent)" }} />
                  <div>
                    <p className="text-xs" style={{ color: "var(--color-text-dim)" }}>Total Stories</p>
                    <p className="text-sm font-bold" style={{ color: "var(--color-text)" }} data-testid="total-stories">
                      {digestPreview.total_stories} ({digestPreview.breaking_items.length} breaking + {digestPreview.top_articles.length} top)
                    </p>
                  </div>
                </div>
              </div>

              {/* Email preview card */}
              <div className="rounded-xl overflow-hidden" style={{ backgroundColor: "#1a1d2e", border: "1px solid #334155" }} data-testid="digest-email-preview">
                <div className="px-6 py-4" style={{ borderBottom: "1px solid #334155" }}>
                  <p className="text-xs font-bold tracking-wider uppercase mb-1" style={{ color: "#64748b" }}>Email Preview</p>
                  <p className="text-sm font-bold" style={{ color: "#f1f5f9" }}>
                    Subject: Daily News Update — {digestPreview.breaking_items.length > 0 ? 'Breaking News + Top Stories' : "Today's Top Stories from Around the World"}
                  </p>
                </div>

                <div className="p-6">
                  <div style={{ background: "#3b82f6", color: "#fff", padding: "6px 12px", borderRadius: "6px", display: "inline-block", fontWeight: "bold", fontSize: "11px", letterSpacing: "2px", marginBottom: "12px" }}>
                    DAILY NEWS UPDATE
                  </div>
                  <h3 className="text-lg font-bold mb-1" style={{ color: "#f1f5f9" }}>Your Daily News Update</h3>
                  <p className="text-xs mb-5" style={{ color: "#64748b" }}>
                    {digestPreview.total_stories} stor{digestPreview.total_stories === 1 ? 'y' : 'ies'} curated for you
                  </p>

                  {/* Breaking news preview */}
                  {digestPreview.breaking_items.length > 0 && (
                    <div className="mb-5">
                      <div style={{ background: "#ef4444", color: "#fff", padding: "4px 10px", borderRadius: "4px", display: "inline-block", fontWeight: "bold", fontSize: "10px", letterSpacing: "2px", marginBottom: "10px" }}>
                        BREAKING NEWS
                      </div>
                      {digestPreview.breaking_items.map((item, idx) => (
                        <div key={`breaking-${idx}`} className="mb-2 p-3 rounded-lg" style={{ background: "#1e293b", borderLeft: "3px solid #ef4444" }} data-testid={`preview-breaking-${idx}`}>
                          <p className="text-sm font-semibold mb-1" style={{ color: "#f1f5f9" }}>{item.title}</p>
                          <p className="text-xs" style={{ color: "#94a3b8" }}>{item.message?.slice(0, 150)}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Top stories preview */}
                  {digestPreview.top_articles.length > 0 && (
                    <div>
                      <div style={{ background: "#3b82f6", color: "#fff", padding: "4px 10px", borderRadius: "4px", display: "inline-block", fontWeight: "bold", fontSize: "10px", letterSpacing: "2px", marginBottom: "10px" }}>
                        TOP STORIES
                      </div>
                      {digestPreview.top_articles.map((article, idx) => (
                        <div key={`article-${idx}`} className="mb-2 p-3 rounded-lg" style={{ background: "#1e293b", borderLeft: "3px solid #3b82f6" }} data-testid={`preview-article-${idx}`}>
                          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase mb-1" style={{ background: "#334155", color: "#94a3b8" }}>
                            {article.category}
                          </span>
                          <p className="text-sm font-semibold mb-1" style={{ color: "#f1f5f9" }}>{article.title}</p>
                          <p className="text-xs mb-1" style={{ color: "#94a3b8" }}>{article.description?.slice(0, 150)}</p>
                          <p className="text-[10px]" style={{ color: "#64748b" }}>{article.source}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Timezone Breakdown */}
              {digestPreview.timezone_breakdown && digestPreview.timezone_breakdown.length > 0 && (
                <div className="mt-6 rounded-xl overflow-hidden" style={{ border: "1px solid var(--color-border)" }} data-testid="timezone-breakdown">
                  <div className="px-5 py-3" style={{ backgroundColor: "var(--color-card-hover)", borderBottom: "1px solid var(--color-border)" }}>
                    <p className="text-sm font-bold" style={{ color: "var(--color-text)" }}>
                      <Globe className="w-4 h-4 inline mr-2" style={{ color: "var(--color-accent)" }} />
                      Timezone Delivery Schedule
                    </p>
                  </div>
                  <div className="divide-y" style={{ borderColor: "var(--color-border)" }}>
                    {digestPreview.timezone_breakdown.map((tz, idx) => (
                      <div key={tz.timezone} className="flex items-center justify-between px-5 py-3" data-testid={`tz-row-${idx}`}>
                        <div>
                          <p className="text-sm font-medium" style={{ color: "var(--color-text)" }}>{tz.timezone}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-xs font-medium px-2 py-1 rounded" style={{ backgroundColor: "var(--color-card-hover)", color: "var(--color-text-muted)" }}>
                            {tz.subscriber_count} subscriber{tz.subscriber_count !== 1 ? 's' : ''}
                          </span>
                          <span className="text-xs font-bold" style={{ color: "var(--color-accent)" }}>
                            Next: {tz.next_send} • {tz.next_date}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-center py-8" style={{ color: "var(--color-text-dim)" }}>No preview available</p>
          )}
        </div>

        {/* Read Tracking Stats */}
        <div className="rounded-xl p-8 mb-8" style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }} data-testid="read-stats-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "#10b981" }}>
              <Eye className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-black" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
                Read Tracking
              </h2>
              <p className="text-sm" style={{ color: "var(--color-text-dim)" }}>
                Track how subscribers engage with digest emails
              </p>
            </div>
          </div>

          {readStats ? (
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <div className="px-4 py-3 rounded-lg" style={{ backgroundColor: "var(--color-card-hover)", border: "1px solid var(--color-border)" }}>
                  <p className="text-xs" style={{ color: "var(--color-text-dim)" }}>Total Clicks (All Time)</p>
                  <p className="text-xl font-black" style={{ color: "var(--color-text)" }} data-testid="total-clicks">{readStats.total_clicks}</p>
                </div>
                <div className="px-4 py-3 rounded-lg" style={{ backgroundColor: "var(--color-card-hover)", border: "1px solid var(--color-border)" }}>
                  <p className="text-xs" style={{ color: "var(--color-text-dim)" }}>Clicks (Last 7 Days)</p>
                  <p className="text-xl font-black" style={{ color: "var(--color-accent)" }} data-testid="recent-clicks">{readStats.recent_clicks}</p>
                </div>
                <div className="px-4 py-3 rounded-lg" style={{ backgroundColor: "var(--color-card-hover)", border: "1px solid var(--color-border)" }}>
                  <p className="text-xs" style={{ color: "var(--color-text-dim)" }}>Unique Readers (7 Days)</p>
                  <p className="text-xl font-black" style={{ color: "#10b981" }} data-testid="unique-readers">{readStats.unique_readers_7d}</p>
                </div>
              </div>

              {readStats.top_articles && readStats.top_articles.length > 0 && (
                <div>
                  <p className="text-sm font-bold mb-3" style={{ color: "var(--color-text)" }}>Top Clicked Articles</p>
                  <div className="space-y-2">
                    {readStats.top_articles.map((article, idx) => (
                      <div key={`top-${idx}`} className="flex items-center justify-between px-4 py-2 rounded-lg" style={{ backgroundColor: "var(--color-card-hover)" }} data-testid={`top-click-${idx}`}>
                        <a href={article.url} target="_blank" rel="noopener noreferrer" className="text-xs truncate flex-1 mr-3 hover:underline" style={{ color: "var(--color-accent)" }}>
                          {article.url}
                        </a>
                        <span className="text-xs font-bold shrink-0 px-2 py-1 rounded" style={{ backgroundColor: "var(--color-accent-glow)", color: "var(--color-accent)" }}>
                          {article.clicks} click{article.clicks !== 1 ? 's' : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {readStats.total_clicks === 0 && (
                <p className="text-sm text-center py-4" style={{ color: "var(--color-text-dim)" }}>
                  No digest clicks tracked yet. Stats will appear after the next digest is sent.
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--color-accent)" }} />
            </div>
          )}
        </div>

        {/* Subscriber Management */}
        <div className="rounded-xl p-8" style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-black mb-2" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
                Subscribers
              </h2>
              <p style={{ color: "var(--color-text-muted)" }}>Manage subscribers and download email lists</p>
            </div>
            <Button onClick={downloadCSV} className="text-white flex items-center gap-2" style={{ backgroundColor: "var(--color-accent)" }} data-testid="download-csv-button">
              <Download className="w-4 h-4" /> Download CSV
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="rounded-lg p-6" style={{ backgroundColor: "var(--color-card-hover)" }}>
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-8 h-8" style={{ color: "var(--color-accent)" }} />
                <h3 className="text-sm font-medium" style={{ color: "var(--color-text-dim)" }}>Total Subscribers</h3>
              </div>
              <p className="text-3xl font-bold" style={{ color: "var(--color-text)" }}>{subscribers.length}</p>
            </div>
            <div className="rounded-lg p-6" style={{ backgroundColor: "var(--color-card-hover)" }}>
              <div className="flex items-center gap-3 mb-2">
                <Mail className="w-8 h-8" style={{ color: "var(--color-live)" }} />
                <h3 className="text-sm font-medium" style={{ color: "var(--color-text-dim)" }}>Email Enabled</h3>
              </div>
              <p className="text-3xl font-bold" style={{ color: "var(--color-text)" }}>
                {subscribers.filter(s => s.notification_preferences?.email_enabled).length}
              </p>
            </div>
            <div className="rounded-lg p-6" style={{ backgroundColor: "var(--color-card-hover)" }}>
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-8 h-8" style={{ color: "var(--color-accent)" }} />
                <h3 className="text-sm font-medium" style={{ color: "var(--color-text-dim)" }}>Active Users</h3>
              </div>
              <p className="text-3xl font-bold" style={{ color: "var(--color-text)" }}>{subscribers.length}</p>
            </div>
          </div>

          {/* Subscriber List */}
          <div>
            <h3 className="text-xl font-bold mb-4" style={{ color: "var(--color-text)" }}>Subscriber List</h3>
            {loading ? (
              <div className="text-center py-8" style={{ color: "var(--color-text-dim)" }}>Loading...</div>
            ) : subscribers.length === 0 ? (
              <div className="text-center py-8" style={{ color: "var(--color-text-dim)" }}>No subscribers yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: "var(--color-text-dim)" }}>#</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: "var(--color-text-dim)" }}>Name</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: "var(--color-text-dim)" }}>Email</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: "var(--color-text-dim)" }}>Joined</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: "var(--color-text-dim)" }}>Email Alerts</th>
                      <th className="text-left py-3 px-4 font-semibold" style={{ color: "var(--color-text-dim)" }}>Frequency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((subscriber, idx) => (
                      <tr key={subscriber.email} style={{ borderBottom: "1px solid var(--color-border)" }}>
                        <td className="py-3 px-4" style={{ color: "var(--color-text-muted)" }}>{idx + 1}</td>
                        <td className="py-3 px-4 font-medium" style={{ color: "var(--color-text)" }}>{subscriber.name}</td>
                        <td className="py-3 px-4" style={{ color: "var(--color-text-muted)" }}>{subscriber.email}</td>
                        <td className="py-3 px-4" style={{ color: "var(--color-text-dim)" }}>{new Date(subscriber.created_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4">
                          {subscriber.notification_preferences?.email_enabled
                            ? <span style={{ color: "var(--color-live)" }}>Enabled</span>
                            : <span className="text-red-400">Disabled</span>}
                        </td>
                        <td className="py-3 px-4" style={{ color: "var(--color-text-muted)" }}>{subscriber.notification_preferences?.frequency || 'instant'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
